import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { ControleEstoqueInventarioPrintRequest } from '../../../../../shared/services/requests/produto/controle-estoque-inventario-print.request';
import { ControleEstoquePrintStruct } from '../../../../../shared/services/structs/produto/controle-estoque-print.struct';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';

export interface ImpressaoInventarioFiltrarProdutosData {
  filtros: ControleEstoqueInventarioPrintRequest;
  exibirSaldoAtual: boolean;
}

interface DisplayRow {
  produto: ControleEstoquePrintStruct;
  isVariacao: boolean;
  isLocal: boolean;
  localizacao?: string;
  saldoFisico?: number;
  saldoDisponivel?: number;
}

@Component({
  selector: 'app-impressao-inventario-filtrar-produtos',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    LoadingBoxComponent,
    AbntPipe,
  ],
  templateUrl: './impressao-inventario-filtrar-produtos.component.html',
  styleUrls: ['./impressao-inventario-filtrar-produtos.component.css'],
})
export class ImpressaoInventarioFiltrarProdutosComponent implements OnInit {

  public isLoading: boolean = false;
  public relatorio: ControleEstoquePrintStruct[] = [];
  public linhas: DisplayRow[] = [];

  public get displayedColumns(): string[] {
    return ['descricao', 'codigoSku', 'unidade', 'preco', 'localizacao', 'saldoFisico'];
  }

  constructor(
    public dialogRef: MatDialogRef<ImpressaoInventarioFiltrarProdutosComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpressaoInventarioFiltrarProdutosData,
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.buscarRelatorio();
  }

  private buscarRelatorio(): void {
    this.isLoading = true;
    const request = new ControleEstoqueInventarioPrintRequest();
    Object.assign(request, this.data.filtros);

    this.controleEstoqueService.getInventarioPrint(request).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (!res.isError) {
          this.relatorio = res.list || [];
          this.montarLinhas();
        } else {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          this.fechar();
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao gerar relatório de inventário.', AlertType.error);
        this.fechar();
      },
    });
  }

  private montarLinhas(): void {
    const linhas: DisplayRow[] = [];

    const adicionarLocais = (entidade: ControleEstoquePrintStruct, isVariacao: boolean): void => {
      if (entidade.locais?.length > 1) {
        for (const local of entidade.locais) {
          linhas.push({
            produto: entidade,
            isVariacao,
            isLocal: true,
            localizacao: local.localizacao || local.codigoLocalizacao || 'Local não informado',
            saldoFisico: local.saldoFisico,
            saldoDisponivel: local.saldoDisponivel,
          });
        }
      }
    };

    for (const produto of this.relatorio) {
      linhas.push({ produto, isVariacao: false, isLocal: false });

      if (produto.variacoes?.length) {
        // Nível 2: variações; Nível 3: locais de cada variação.
        for (const variacao of produto.variacoes) {
          linhas.push({ produto: variacao, isVariacao: true, isLocal: false });
          adicionarLocais(variacao, true);
        }
      } else {
        adicionarLocais(produto, false);
      }
    }

    this.linhas = linhas;
  }

  public getLocalizacao(row: DisplayRow): string {
    if (row.isLocal) return row.localizacao || 'Local não informado';
    return row.produto.localizacao || 'Sem local';
  }

  public getSaldo(row: DisplayRow): number {
    if (this.data.exibirSaldoAtual) {
      return row.isLocal ? (row.saldoDisponivel ?? 0) : row.produto.saldoDisponivel;
    }
    return row.isLocal ? (row.saldoFisico ?? 0) : row.produto.saldoFisico;
  }

  public imprimir(): void {
    const conteudoParaImprimir = document.querySelector('.modal-print-container');
    if (!conteudoParaImprimir) return;

    const janelaImpressao = window.open('', '', 'height=700,width=1100');
    if (!janelaImpressao) {
      this.alertService.show('Aviso', 'Permita pop-ups para imprimir.', AlertType.warning);
      return;
    }

    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules).map(rule => rule.cssText).join('');
        } catch {
          return '';
        }
      })
      .join('');

    janelaImpressao.document.write(`
      <html>
        <head>
          <title>Imprimir Inventário de Produtos</title>
          <base href="/">
          <style>
            ${styles}
            html, body {
                height: auto !important;
                min-height: 0 !important;
                overflow: visible !important;
                margin: 20px;
                font-family: Roboto, "Helvetica Neue", sans-serif;
                color: #000;
                background: #fff;
            }
            .no-print { display: none !important; }
            .modal-print-container {
                display: block !important;
                width: 100% !important;
                height: auto !important;
                min-height: 0 !important;
                overflow: visible !important;
                box-shadow: none !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .mat-mdc-table { background: white !important; border-spacing: 0 !important; }
            .mat-mdc-header-cell, .mat-mdc-cell { border: 0 !important; color: black !important; }
            .mat-mdc-header-cell { font-weight: bold !important; border-bottom: 2px solid black !important; }
            .mat-mdc-cell { border-bottom: 1px solid #888 !important; }
            .title-header { text-align: center; margin-bottom: 20px; }
            h2 { font-size: 16px; margin: 0; }
          </style>
        </head>
        <body>
          ${conteudoParaImprimir.innerHTML}
        </body>
      </html>
    `);

    janelaImpressao.document.close();
    janelaImpressao.focus();

    setTimeout(() => {
      janelaImpressao.print();
      janelaImpressao.close();
    }, 800);
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
